import React, { useEffect, useMemo, useState } from "react"
import { Modal, Form, Input, Button, Divider, Switch, Tabs } from "antd"
import { notifications } from "notifications"
import { useStore } from "stores/utils/use-store"
import {
  CreateGamePlatform,
  GamePlatformType,
  GamePublisherOption,
  NativeGame,
} from "../store/types"
import { gamePlatformOptions } from "../constants"
import { getGamePublishers } from "../api/native-games-control.call"
import { PlatformFormFields } from "./platform-form-fields"
import { PlatformTabLabel } from "./platform-tab-label"

type Props = {
  editGame: NativeGame | null
  setEditGame: React.Dispatch<React.SetStateAction<NativeGame | null>>
  setSelectedGame: React.Dispatch<React.SetStateAction<NativeGame | null>>
}

const GLOBAL_TAB_KEY = "__global__"

/**
 * Edit modal for an existing native game.
 *
 * The tab list lives in local React state (`tabPlatforms`) so adding a tab via
 * `+` is synchronous and `activeKey` always matches an item. Form values for
 * the platform fields live in Antd's form store on ["platforms", idx, field]
 * paths — kept in sync with tabPlatforms on add/remove. We deliberately avoid
 * Form.useWatch for the tabs UI because it can lag a render behind
 * setFieldValue on a previously-unset path, which led to the new tab's
 * content area rendering empty.
 *
 * <Modal destroyOnClose> forces a fresh mount on each open so form state
 * can't bleed between sessions.
 */
export const EditGameInfoModal: React.FC<Props> = ({
  editGame,
  setEditGame,
  setSelectedGame,
}) => {
  const [publishers, setPublishers] = useState<GamePublisherOption[]>([])
  const [activeTab, setActiveTab] = useState<string>(GLOBAL_TAB_KEY)
  // Source of truth for the tab strip. The form store separately holds the
  // actual per-platform field values at ["platforms", idx, ...] paths.
  const [tabPlatforms, setTabPlatforms] = useState<{ platform: GamePlatformType }[]>([])
  const { dataStore: { nativeGamesControlStore } } = useStore()
  const [form] = Form.useForm()

  const originalPlatforms = useMemo(
    () => new Set((editGame?.platforms ?? []).map(p => p.platform)),
    [editGame],
  )

  useEffect(() => {
    if (editGame) {
      getGamePublishers()
        .then(res => res && setPublishers(res))
        .catch(err => notifications.error(err?.message || "Failed to load publishers"))
    }
  }, [editGame])

  // Re-seed the form and the local tabs state when the modal opens for a
  // *different* game. Keyed on editGame.id so unrelated parent re-renders
  // don't re-run setFieldsValue and overwrite whatever the admin is typing.
  useEffect(() => {
    if (editGame) {
      form.resetFields()
      form.setFieldsValue({
        ...editGame,
        platforms: (editGame.platforms || []).map(platform => ({
          ...platform,
          gamePublisherId: platform.gamePublisher?.id,
        })),
      })
      setTabPlatforms((editGame.platforms || []).map(p => ({ platform: p.platform })))
      setActiveTab(GLOBAL_TAB_KEY)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editGame?.id])

  const availablePlatforms = useMemo(() => {
    const present = new Set<string>(tabPlatforms.map(p => p.platform))

    return gamePlatformOptions.filter(opt => !present.has(opt.value))
  }, [tabPlatforms])

  const onFinishHandler = async(values: any) => {
    if (!editGame) return
    try {
      const parsedValues = {
        ...values,
        description: values.description || null,
        videoBannerUrl: values.videoBannerUrl || null,
        earningSpeed: Number(values.earningSpeed),
        platforms: values.platforms?.map((platform: CreateGamePlatform) => ({
          ...platform,
          adjustLinkToken: platform.adjustLinkToken || null,
          rating: Number(platform.rating),
          installRevenue: Number(platform.installRevenue),
          appsflyerRevenue: platform.appsflyerRevenue != null && (platform.appsflyerRevenue as any) !== ""
            ? Number(platform.appsflyerRevenue)
            : null,
          attributionWindowDays: platform.attributionWindowDays != null && (platform.attributionWindowDays as any) !== ""
            ? Number(platform.attributionWindowDays)
            : null,
          dailyBudget: platform.dailyBudget != null && (platform.dailyBudget as any) !== ""
            ? Number(platform.dailyBudget)
            : undefined,
          monthlyBudget: platform.monthlyBudget != null && (platform.monthlyBudget as any) !== ""
            ? Number(platform.monthlyBudget)
            : undefined,
        })) || [],
      }

      await nativeGamesControlStore.updateNativeGame(editGame!.id, parsedValues)
      // Optimistic refresh of the parent details view. The form sends
      // `gamePublisherId` (flat string) but the parent stores
      // `gamePublisher: {id, name}` for display — re-attach by looking up
      // the publisher in the loaded list, falling back to the previous
      // platform's gamePublisher (no-op publisher change) so the publisher
      // name doesn't disappear from the details view until the next refetch.
      setSelectedGame(prev => {
        if (!prev) return prev
        const stitchedPlatforms = parsedValues.platforms.map((p: any) => {
          const pub = publishers.find(opt => opt.id === p.gamePublisherId)
          const previous = prev.platforms.find(orig => orig.platform === p.platform)

          return {
            ...p,
            gamePublisher: pub
              ? { id: pub.id, name: pub.name, costReportingPlatform: previous?.gamePublisher?.costReportingPlatform }
              : previous?.gamePublisher,
          }
        })

        return { ...prev, ...parsedValues, platforms: stitchedPlatforms }
      })
      notifications.success("Game updated successfully")
      setEditGame(null)
    } catch (err: any) {
      notifications.error(err?.message || err)
    }
  }

  const handleCancel = () => {
    form.resetFields()
    setEditGame(null)
  }

  const onFinishFailed = ({ errorFields }: { errorFields: { name: (string | number)[] }[] }) => {
    if (!errorFields?.length) return
    const firstPath = errorFields[0].name
    if (Array.isArray(firstPath) && firstPath[0] === "platforms" && typeof firstPath[1] === "number") {
      const idx = firstPath[1]
      const platform = form.getFieldValue(["platforms", idx, "platform"])
      if (platform) setActiveTab(platform)
    } else {
      setActiveTab(GLOBAL_TAB_KEY)
    }
    notifications.error("Please fix the highlighted fields before saving")
  }

  const handleTabEdit = (
    targetKey: React.MouseEvent | React.KeyboardEvent | string,
    action: "add" | "remove",
  ) => {
    if (action === "add") {
      const nextPlatform = availablePlatforms[0]?.value as GamePlatformType | undefined
      if (!nextPlatform) {
        notifications.error("No more platforms available to add")

        return
      }
      const nextTabPlatforms = [...tabPlatforms, { platform: nextPlatform }]
      setTabPlatforms(nextTabPlatforms)
      const current: any[] = form.getFieldValue("platforms") || []
      form.setFieldValue("platforms", [
        ...current,
        { platform: nextPlatform, isInstallable: true, isBudgetActive: false },
      ])
      setActiveTab(nextPlatform)
    } else if (action === "remove" && typeof targetKey === "string") {
      const idx = tabPlatforms.findIndex(p => p.platform === targetKey)
      if (idx < 0) return
      setTabPlatforms(tabPlatforms.filter((_, i) => i !== idx))
      const current: any[] = form.getFieldValue("platforms") || []
      form.setFieldValue("platforms", current.filter((_, i) => i !== idx))
      setActiveTab(GLOBAL_TAB_KEY)
    }
  }

  return (
    <Modal
      open={!!editGame}
      destroyOnClose
      title="Edit Game"
      onCancel={handleCancel}
      footer={null}
      width={780}
      bodyStyle={{ paddingTop: 0, paddingBottom: 12 }}
    >
      <Form layout="vertical" form={form} onFinish={onFinishHandler} onFinishFailed={onFinishFailed}>
        <Tabs
          type="editable-card"
          hideAdd={availablePlatforms.length === 0}
          activeKey={activeTab}
          onChange={setActiveTab}
          onEdit={handleTabEdit}
          items={[
            {
              key: GLOBAL_TAB_KEY,
              label: "Global",
              closable: false,
              children: (
                <>
                  <Divider orientation="left">Game Details</Divider>
                  <Form.Item
                    label="Name"
                    name="name"
                    rules={[{ required: true, message: "Game name is required" }]}
                  >
                    <Input placeholder="Please enter Game Name" />
                  </Form.Item>
                  <Form.Item
                    label="Category"
                    name="category"
                    rules={[{ required: true, message: "Category is required" }]}
                  >
                    <Input placeholder="Please enter category" />
                  </Form.Item>
                  <Form.Item
                    label="Banner URL"
                    name="bannerUrl"
                    rules={[
                      { required: true, message: "Banner URL is required" },
                      { type: "url", message: "Please enter a valid URL" },
                    ]}
                  >
                    <Input placeholder="Please enter Banner URL" />
                  </Form.Item>
                  <Form.Item
                    label="Video Banner URL"
                    name="videoBannerUrl"
                    rules={[{ type: "url", message: "Please enter a valid URL" }]}
                  >
                    <Input placeholder="Please enter Video Banner URL" />
                  </Form.Item>
                  <Form.Item
                    label="Earning Speed"
                    name="earningSpeed"
                    rules={[{ required: true, message: "Earning Speed is required" }]}
                  >
                    <Input type="number" placeholder="Please enter Earning Speed" min={1} />
                  </Form.Item>
                  <Form.Item label="Description" name="description">
                    <Input.TextArea placeholder="Please enter description" rows={3} />
                  </Form.Item>
                  <Form.Item label="Is Top Pick" name="isTopPick" valuePropName="checked">
                    <Switch />
                  </Form.Item>
                  <Form.Item label="Engaging users only" name="isOnlyForEngagingAccount" valuePropName="checked">
                    <Switch />
                  </Form.Item>
                </>
              ),
            },
            ...tabPlatforms.map((p, idx) => {
              const isOriginal = originalPlatforms.has(p.platform)
              const platformChoices = isOriginal
                ? gamePlatformOptions
                : [
                  ...availablePlatforms,
                  ...gamePlatformOptions.filter(opt => opt.value === p.platform),
                ]

              return {
                key: p.platform,
                label: (
                  <PlatformTabLabel
                    platform={p.platform}
                    isInstallable={form.getFieldValue(["platforms", idx, "isInstallable"]) ?? true}
                  />
                ),
                closable: !isOriginal,
                forceRender: true,
                children: (
                  <PlatformFormFields
                    index={idx}
                    publishers={publishers}
                    platformLocked={isOriginal}
                    availablePlatforms={platformChoices}
                  />
                ),
              }
            }),
          ]}
        />

        {availablePlatforms.length === 0 && tabPlatforms.length > 0 && (
          <div style={{ fontSize: 12, color: "#999", marginTop: 8 }}>
            All available platforms are configured. Remove one to add a different platform.
          </div>
        )}
        {availablePlatforms.length > 0 && (
          <div style={{ fontSize: 12, color: "#999", marginTop: 8 }}>
            Click <strong>+</strong> on the tab strip to add{" "}
            {availablePlatforms.map(p => p.label).join(" / ")} for this game.
          </div>
        )}

        <Form.Item>
          <div style={{ display: "flex", justifyContent: "space-between", marginTop: 12 }}>
            <Button onClick={handleCancel}>Cancel</Button>
            <Button type="primary" htmlType="submit">
              Save
            </Button>
          </div>
        </Form.Item>
      </Form>
    </Modal>
  )
}
